import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { ProductsDropdown } from "@/components/products/ProductsDropdown";
import { ConsumptionBreakdown, ProgressCard, SelectedApiKeyUsage, UsagePlanOverview, UtilizationFilters, UtilizationSummaryCards, useUtilizationDeepDiveData } from "@/components/usage";
import { FILTER_VALUES } from "@/constants/filters";
import useAuthStore from "@/context/auth/useAuthStore";
import useUtilizationDeepDiveStore from "@/context/utilization/useUtilizationDeepDiveStore";
import { useEffectiveCustomerId } from "@/hooks/useEffectiveCustomerId";
import { useUsageContractSelection } from "@/hooks/useUsageContractSelection";
import { DashboardPageFilters, DashboardPageLayout, DashboardPageLoading, DashboardPageSection } from "@/layouts/DashboardPageLayout";
export default function UtilizationDeepDivePage() {
    const { productCode } = useAuthStore();
    const { isViewingAllOrgs } = useEffectiveCustomerId();
    const { filters, selectedContractId, setSelectedContractId } = useUtilizationDeepDiveStore();
    const contractSelection = useUsageContractSelection({
        authProductCode: productCode ?? undefined,
        selectedContractId,
        setSelectedContractId
    });
    const activeProductCode = contractSelection.selectedContract?.ProductCode ?? "";
    const resolvedContractId = contractSelection.resolvedSelectedContractId;
    const contractExpiryDate = contractSelection.selectedContract?.ExpiryDate;
    const validContractExpiryDate = contractExpiryDate && !Number.isNaN(new Date(contractExpiryDate).getTime()) ? contractExpiryDate : undefined;
    const customerIdOverride = isViewingAllOrgs ? contractSelection.selectedContract?.CustomerId : undefined;
    const data = useUtilizationDeepDiveData({
        activeProductCode,
        contractId: resolvedContractId,
        contractExpiryDate: validContractExpiryDate,
        contractStartDate: contractSelection.selectedContract?.StartDate,
        customerIdOverride,
        isExpiredContract: contractSelection.selectedIsExpired
    });
    const hasContractOptions = contractSelection.activeOptions.length > 0 || contractSelection.expiredOptions.length > 0;
    const isResolvingContract = hasContractOptions && !resolvedContractId;
    return /*#__PURE__*/ _jsxs(DashboardPageLayout, {
        title: "Utilization",
        description: "Usage patterns, key concentration, and request activity",
        children: [
            /*#__PURE__*/ _jsx(DashboardPageFilters, {
                children: /*#__PURE__*/ _jsx("div", {
                    className: "w-full max-w-xs",
                    children: /*#__PURE__*/ _jsx(ProductsDropdown, {
                        options: contractSelection.activeOptions,
                        error: contractSelection.error,
                        expiredOptions: contractSelection.expiredOptions,
                        isLoading: contractSelection.isLoading,
                        optionType: "contract",
                        showAllOption: false,
                        value: selectedContractId,
                        onChange: setSelectedContractId
                    })
                })
            }),
            (contractSelection.isLoading || isResolvingContract || data.isFiltersLoading) && /*#__PURE__*/ _jsx(DashboardPageLoading, {}),
            !contractSelection.isLoading && !contractSelection.error && !isResolvingContract && !resolvedContractId && /*#__PURE__*/ _jsx(DashboardPageSection, {
                className: "text-center",
                surface: "card",
                padding: "default",
                children: /*#__PURE__*/ _jsx("p", {
                    className: "text-gray-500",
                    children: "No metered contracts are available for utilization data."
                })
            }),
            resolvedContractId && !data.isFiltersLoading && data.hasContract && /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(UtilizationFilters, {
                        filterableApiKeysCount: data.filterableApiKeysCount,
                        includesHistoricalApiKeys: contractSelection.selectedIsExpired,
                        selectableApiKeys: data.selectableApiKeys
                    }),
                    /*#__PURE__*/ _jsx(UtilizationSummaryCards, {
                        avgDailyUsageValue: data.avgDailyUsageValue,
                        avgHourlyUsageValue: data.avgHourlyUsageValue,
                        isLoading: data.isDataLoading,
                        peakPoint: data.peakPoint,
                        totalRequests: data.totalRequests,
                        usageUnit: data.usageUnit
                    }),
                    data.hasError && /*#__PURE__*/ _jsx("div", {
                        className: "rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700",
                        children: data.errorMessage
                    }),
                    filters.selectedApiKey === FILTER_VALUES.ALL ? /*#__PURE__*/ _jsx(ConsumptionBreakdown, {
                        activeApiKeysCount: data.activeApiKeys.length,
                        donutSegments: data.donutSegments,
                        isLoading: data.isDataLoading,
                        keyNameMap: data.keyNameMap,
                        keyUsageTotalsCount: data.keyUsageTotals.length,
                        topTenKeys: data.topTenKeys,
                        totalUsageValue: data.totalUsageValue,
                        usageUnit: data.usageUnit
                    }) : /*#__PURE__*/ _jsx(SelectedApiKeyUsage, {
                        lastUsedAt: data.lastUsedAt,
                        selectedApiKey: filters.selectedApiKey,
                        selectedApiKeyDetails: data.selectedApiKeyDetails,
                        totalRequests: data.totalRequests,
                        totalUsageValue: data.totalUsageValue,
                        trendPoints: data.trendPoints,
                        usageUnit: data.usageUnit
                    }),
                    data.usagePlan && /*#__PURE__*/ _jsx(UsagePlanOverview, {
                        usagePlan: data.usagePlan
                    }),
                    data.usagePlan && !contractSelection.selectedIsExpired && /*#__PURE__*/ _jsx(ProgressCard, {
                        currentApiKeysUsed: data.currentApiKeysUsed,
                        totalConsumptionUsed: data.totalConsumptionUsed,
                        usageUnit: data.consumptionUsageUnit,
                        usagePlan: data.usagePlan,
                        dateRange: data.consumptionDateRange
                    })
                ]
            })
        ]
    });
}
